import { UserCheck, HeartHandshake, Landmark } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { AppFormValues } from "@/project/components/ApplicationForm";

export type PickMember = {
  full_name: string;
  relation?: string | null;
  gender?: string | null;
  age?: number | string | null;
  mobile?: string | null;
  aadhaar?: string | null;
  occupation?: string | null;
};

/** Lets staff push a captured family member straight into the letter fields. */
export function MemberPicker({
  members,
  value,
  onChange,
}: {
  members: PickMember[];
  value: AppFormValues;
  onChange: (v: AppFormValues) => void;
}) {
  const list = members.filter(m => (m.full_name ?? "").trim());
  if (!list.length) {
    return null;
  }

  const asApplicant = (m: PickMember) => {
    onChange({
      ...value,
      applicant_name: m.full_name,
      applicant_mobile: m.mobile ?? value.applicant_mobile,
      applicant_aadhaar: m.aadhaar ?? value.applicant_aadhaar,
    });
    toast.success(`${m.full_name} set as applicant`);
  };

  const asBeneficiary = (m: PickMember) => {
    onChange({
      ...value,
      same_as_applicant: false,
      beneficiary_name: m.full_name,
      beneficiary_relation: m.relation ?? "",
      beneficiary_mobile: m.mobile ?? "",
      beneficiary_aadhaar: m.aadhaar ?? "",
    });
    toast.success(`${m.full_name} set as beneficiary`);
  };

  const asBankHolder = (m: PickMember) => {
    onChange({ ...value, bank_account_holder: m.full_name });
    toast.success(`Bank account holder set to ${m.full_name}`);
  };

  return (
    <Card className="no-print border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Family Members Captured</CardTitle>
        <p className="text-sm text-muted-foreground">
          Tap a role to fill the applicant, beneficiary or bank fields used in
          the letter.
        </p>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {list.map(m => {
          const isApplicant = value.applicant_name === m.full_name;
          const isBeneficiary =
            !value.same_as_applicant && value.beneficiary_name === m.full_name;
          return (
            <div
              key={`${m.full_name}`}
              className="rounded-md border border-border p-3"
            >
              <p className="font-medium">{m.full_name}</p>
              <p className="text-xs text-muted-foreground">
                {[
                  m.relation,
                  m.gender,
                  m.age ? `${m.age} yrs` : "",
                  m.occupation,
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                {m.mobile ?? "No mobile"}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button
                  size="sm"
                  variant={isApplicant ? "default" : "outline"}
                  onClick={() => asApplicant(m)}
                >
                  <UserCheck className="mr-1 h-3.5 w-3.5" /> Applicant
                </Button>
                <Button
                  size="sm"
                  variant={isBeneficiary ? "default" : "outline"}
                  onClick={() => asBeneficiary(m)}
                >
                  <HeartHandshake className="mr-1 h-3.5 w-3.5" /> Beneficiary
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => asBankHolder(m)}
                >
                  <Landmark className="mr-1 h-3.5 w-3.5" /> Bank
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
